import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Tag,
  message,
  Modal,
  Tooltip,
  Dropdown,
  Space,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  RobotOutlined,
  DownOutlined,
  SendOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';
import {
  deleteBot,
  enableBot,
  disableBot,
} from '../../services/api';
import { useChannelsData } from '../../contexts/ChannelsDataContext';
import { getDataCollectionsTablePagination } from '../../components/collections/collectionUtils';
import { relativeTime, fullDateTime } from '../../utils/relativeTime';
import {
  botStatusLabel,
  botStatusColor,
  botTriggerLabels,
  platformLabel,
} from '../../utils/botDisplay';
import SegmentedToggle from '../../components/SegmentedToggle';
import { useChannelScope } from '../../hooks/useChannelScope';
import { useChannelsFilter } from '../../contexts/ChannelsFilterContext';
import '../DataCollections.css';

const { Text } = Typography;

function Bots() {
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();
  const { assistantId: filterAssistantId } = useChannelsFilter() || {};
  const { bots, botsLoading, ensureBots, invalidateBots } = useChannelsData();
  const loading = botsLoading;
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [botToDelete, setBotToDelete] = useState(null);

  const {
    scopeTab,
    setScopeTab,
    assistantById,
    buildScopeTabOptions,
    filterRowsByScope,
  } = useChannelScope();

  const refreshBots = useCallback(async () => {
    if (!initialized || !keycloak?.authenticated || !keycloak.token) return;
    try {
      invalidateBots();
      await ensureBots(true);
    } catch (e) {
      message.error(e.message || 'Failed to load bots');
    }
  }, [initialized, keycloak, ensureBots, invalidateBots]);

  useEffect(() => {
    if (!initialized || !keycloak?.authenticated || !keycloak.token) return;
    ensureBots();
  }, [initialized, keycloak, ensureBots]);

  const filteredBots = useMemo(
    () => filterRowsByScope(bots, filterAssistantId),
    [bots, filterRowsByScope, filterAssistantId]
  );

  const scopeTabOptions = useMemo(
    () => buildScopeTabOptions(bots),
    [bots, buildScopeTabOptions]
  );

  const handleToggleEnabled = async (bot) => {
    try {
      if (bot.enabled) {
        await disableBot(keycloak, bot.id);
        message.success('Bot disabled');
      } else {
        await enableBot(keycloak, bot.id);
        message.success('Bot enabled');
      }
      refreshBots();
    } catch (e) {
      message.error(e.message || 'Failed to update bot');
    }
  };

  const handleDelete = async () => {
    if (!botToDelete) return;
    try {
      await deleteBot(keycloak, botToDelete.id);
      message.success('Bot deleted');
      setDeleteModalVisible(false);
      setBotToDelete(null);
      refreshBots();
    } catch (e) {
      message.error(e.message || 'Failed to delete bot');
    }
  };

  const getEmptyText = () => (
    <div className="empty-state">
      <RobotOutlined className="empty-state-icon" />
      <div className="empty-state-title">No bots yet</div>
      <Button type="primary" className="empty-state-action" onClick={() => navigate('/channels/bots/add')}>
        <PlusOutlined /> Create your first bot
      </Button>
    </div>
  );

  const botPlatformIcon = (platform) => {
    if (platform === 'telegram_userbot') {
      return <SendOutlined className="bot-platform-icon bot-platform-icon-telegram" />;
    }
    return <RobotOutlined className="bot-platform-icon" />;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          {botPlatformIcon(record.platform)}
          <Text strong>{name}</Text>
          {!record.enabled && <Tag>Disabled</Tag>}
        </Space>
      ),
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (p) => platformLabel(p),
    },
    {
      title: 'Assistant',
      key: 'assistant',
      render: (_, record) => {
        const assistant = assistantById.get(record.assistant_id);
        return assistant?.name || record.assistant_name || '—';
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Tag color={botStatusColor(status, record.has_credentials)}>
          {botStatusLabel(status, record.has_credentials)}
        </Tag>
      ),
    },
    {
      title: 'Triggers',
      key: 'triggers',
      render: (_, record) => (
        <Space size={[4, 4]} wrap>
          {botTriggerLabels(record.trigger_config, record.trigger_mode).map((label) => (
            <Tag key={label}>{label}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Last heartbeat',
      dataIndex: 'last_heartbeat_at',
      key: 'last_heartbeat_at',
      render: (v) => (
        <Tooltip title={fullDateTime(v)}>
          <span>{v ? relativeTime(v) : '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 88,
      align: 'center',
      className: 'actions-cell',
      render: (_, record) => {
        const needsConnect = record.platform === 'telegram_userbot' && !record.has_credentials;
        const menuItems = [
          {
            key: 'sessions',
            icon: <MessageOutlined />,
            label: 'Messages',
          },
          ...(needsConnect
            ? [{
              key: 'connect',
              icon: <SendOutlined />,
              label: 'Connect Telegram',
            }]
            : []),
          {
            key: 'toggle',
            icon: record.enabled ? <PauseCircleOutlined /> : <PlayCircleOutlined />,
            label: record.enabled ? 'Disable' : 'Enable',
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
          },
        ];
        return (
          <div
            className="collections-list-actions-toolbar assistants-actions-toolbar"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Space.Compact>
              <Tooltip title="Edit">
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined />}
                  aria-label="Edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/channels/bots/${record.id}/edit`);
                  }}
                />
              </Tooltip>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: menuItems,
                  onClick: ({ key, domEvent }) => {
                    domEvent?.stopPropagation();
                    if (key === 'connect') navigate(`/channels/bots/${record.id}/connect`);
                    else if (key === 'sessions') navigate(`/channels/bots/${record.id}/activity`);
                    else if (key === 'toggle') handleToggleEnabled(record);
                    else if (key === 'delete') {
                      setBotToDelete(record);
                      setDeleteModalVisible(true);
                    }
                  },
                }}
              >
                <Tooltip title="More actions">
                  <Button
                    type="default"
                    size="small"
                    icon={<DownOutlined />}
                    aria-label="More actions"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Dropdown>
            </Space.Compact>
          </div>
        );
      },
    },
  ];

  return (
    <div className="page-container">
      <div className="collections-page-header">
        <div className="page-header">
          <h2 className="page-header-title">Bots</h2>
          <span className="page-header-subtitle">Delivery channels for your assistants</span>
        </div>
        <Button
          type="primary"
          className="new-collection-btn"
          icon={<PlusOutlined />}
          onClick={() => navigate('/channels/bots/add')}
        >
          Create Bot
        </Button>
      </div>

      <SegmentedToggle options={scopeTabOptions} value={scopeTab} onChange={setScopeTab} />

      <div className="collections-table-wrapper collections-table-animate">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredBots}
          columns={columns}
          pagination={getDataCollectionsTablePagination(10, { totalItemLabel: 'bots' })}
          locale={{ emptyText: getEmptyText() }}
        />
      </div>

      <Modal
        title="Delete bot"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setBotToDelete(null);
        }}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Delete bot <strong>{botToDelete?.name}</strong>? This cannot be undone.
        </p>
      </Modal>
    </div>
  );
}

export default Bots;
